'use client'

import { Button } from '@tailark/core/ui/button'
import { Menu, X } from 'lucide-react'
import Link from 'next/link'
import React from 'react'
import { ListItem } from '../components/ListItem'
import { Logo } from '../components/Logo'
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '../components/NavigationMenu'
import { cn } from '../../lib/utils'
import { resolveAppBaseUrl } from '../resolve-app-url'
import type { CommonLocale, CommonTranslations } from '../types/site-copy'

type HeaderMenuItem = {
    name: string
    href: string
    hasDropdown?: boolean
    hasDocs?: boolean
}

type HeaderSolutionItem = {
    icon: string
    title: string
    href: string
    description: string
}

type HeaderDocsItem = {
    name: string
    href: string
    description: string
}

interface HeaderProps {
    locale: CommonLocale
    translations: CommonTranslations
    routePrefix?: string
    menuItems?: HeaderMenuItem[]
    solutionItems?: HeaderSolutionItem[]
    docsItems?: HeaderDocsItem[]
    homeHref?: string
    docsHref?: string
    salesDealRoomHref?: string
    openMenuLabel?: string
    closeMenuLabel?: string
    showAuthActions?: boolean
    showCtaOnScroll?: boolean
}

const withPrefix = (routePrefix: string, path: string): string => `${routePrefix}${path}`

export function HeroHeader({
    locale,
    translations,
    routePrefix = '',
    menuItems,
    solutionItems,
    docsItems,
    homeHref,
    docsHref = '/docs',
    salesDealRoomHref,
    openMenuLabel = 'Open Menu',
    closeMenuLabel = 'Close Menu',
    showAuthActions = true,
    showCtaOnScroll = true,
}: HeaderProps) {
    const t = translations
    const dir = locale === 'fa' ? 'rtl' : 'ltr'
    const resolvedHomeHref = homeHref ?? withPrefix(routePrefix, '/')
    const resolvedSalesDealRoomHref = salesDealRoomHref ?? withPrefix(routePrefix, '/sales-deal-room')

    const resolvedMenuItems = menuItems ?? [
        { name: t.nav.features, href: withPrefix(routePrefix, '/features') },
        { name: t.nav.solution, href: withPrefix(routePrefix, '/'), hasDropdown: true },
        { name: t.nav.docs, href: docsHref, hasDocs: true },
        { name: t.nav.pricing, href: withPrefix(routePrefix, '/pricing') },
        { name: t.nav.templates, href: withPrefix(routePrefix, '/templates') },
        { name: t.nav.about, href: withPrefix(routePrefix, '/about') },
    ]
    const resolvedSolutionItems = solutionItems ?? [
        { icon: '/icons/one.svg', title: t.solutions.salesDealRoom.title, href: withPrefix(routePrefix, '/sales-deal-room'), description: t.solutions.salesDealRoom.description },
        { icon: '/icons/two.svg', title: t.solutions.customerOnboarding.title, href: withPrefix(routePrefix, '/customer-onboarding'), description: t.solutions.customerOnboarding.description },
        { icon: '/icons/three.svg', title: t.solutions.formBuilder.title, href: withPrefix(routePrefix, '/form-builder'), description: t.solutions.formBuilder.description },
    ]
    const resolvedDocsItems = docsItems ?? [
        { name: t.docs.caseStudies, href: docsHref, description: t.docs.caseStudiesDesc },
        { name: t.docs.tutorials, href: `${docsHref}/tutorials`, description: t.docs.tutorialsDesc },
        { name: t.docs.guides, href: `${docsHref}/guides`, description: t.docs.guidesDesc },
        { name: t.docs.faq, href: `${docsHref}/faq`, description: t.docs.faqDesc },
    ]
    const appBaseUrl = resolveAppBaseUrl(locale === 'fa' ? 'ir' : 'com')
    const loginUrl = `${appBaseUrl}/auth/login`
    const signUpUrl = `${appBaseUrl}/auth/register`
    const appRedirectUrl = `${appBaseUrl}/`
    const [menuState, setMenuState] = React.useState(false)
    const [isScrolled, setIsScrolled] = React.useState(false)

    React.useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50)
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <header>
            <nav data-state={menuState && 'active'} className="fixed z-20 w-full px-2">
                <div className={cn('mx-auto mt-2 max-w-6xl px-6 transition-all duration-300 lg:px-12', isScrolled && 'bg-background/50 max-w-4xl rounded-2xl border backdrop-blur-lg lg:px-5')}>
                    <div className="relative flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-4">
                        <div className="flex w-full justify-between lg:w-auto">
                            <Link href={resolvedHomeHref} aria-label="home" className="flex items-center space-x-2">
                                <Logo />
                            </Link>
                            <button onClick={() => setMenuState(!menuState)} aria-label={menuState ? closeMenuLabel : openMenuLabel} className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden">
                                <Menu className="in-data-[state=active]:rotate-180 in-data-[state=active]:scale-0 in-data-[state=active]:opacity-0 m-auto size-6 duration-200" />
                                <X className="in-data-[state=active]:rotate-0 in-data-[state=active]:scale-100 in-data-[state=active]:opacity-100 absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200" />
                            </button>
                        </div>

                        <div className="absolute inset-0 m-auto hidden size-fit lg:block">
                            <NavigationMenu dir={dir}>
                                <NavigationMenuList className="flex flex-row gap-4">
                                    {resolvedMenuItems.map((item) => (
                                        <NavigationMenuItem key={item.href}>
                                            {item.hasDropdown || item.hasDocs ? (
                                                <>
                                                    <NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground border-none bg-transparent shadow-none hover:bg-transparent">
                                                        {item.name}
                                                    </NavigationMenuTrigger>
                                                    <NavigationMenuContent className="p-4">
                                                        <ul className="grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                                                            <li className="row-span-3" hidden={item.hasDocs}>
                                                                <NavigationMenuLink asChild>
                                                                    <a className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-gradient-to-b p-6 no-underline outline-none focus:shadow-md" href={resolvedSalesDealRoomHref}>
                                                                        <div className="mb-2 mt-4 text-lg font-medium">{t.solutions.title}</div>
                                                                        <p className="text-muted-foreground text-sm leading-tight">{t.solutions.description}</p>
                                                                    </a>
                                                                </NavigationMenuLink>
                                                            </li>

                                                            {item.hasDropdown &&
                                                                resolvedSolutionItems.map((solutionItem) => (
                                                                    <ListItem key={solutionItem.href} href={solutionItem.href} title={solutionItem.title}>
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="m-1 size-10 flex-none overflow-hidden rounded bg-white shadow-[0_1px_1px_rgba(0,0,0,0.05),0_0_0_1px_rgba(34,42,53,0.05),0_4px_6px_rgba(34,42,53,0.04),0_24px_68px_rgba(47,48,55,0.05),0_2px_3px_rgba(0,0,0,0.04)]">
                                                                                <img src={solutionItem.icon} alt={solutionItem.title} width={40} height={40} />
                                                                            </div>
                                                                            {solutionItem.description}
                                                                        </div>
                                                                    </ListItem>
                                                                ))}

                                                            {item.hasDocs &&
                                                                resolvedDocsItems.map((docsItem) => (
                                                                    <ListItem key={docsItem.href} href={docsItem.href} title={docsItem.name}>
                                                                        {docsItem.description}
                                                                    </ListItem>
                                                                ))}
                                                        </ul>
                                                    </NavigationMenuContent>
                                                </>
                                            ) : (
                                                <Button asChild variant="ghost" className="text-muted-foreground hover:text-accent-foreground font-normal">
                                                    <Link href={item.href} className="block duration-150">
                                                        <span>{item.name}</span>
                                                    </Link>
                                                </Button>
                                            )}
                                        </NavigationMenuItem>
                                    ))}
                                </NavigationMenuList>
                            </NavigationMenu>
                        </div>

                        <div className="bg-background in-data-[state=active]:block lg:in-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
                            <div className="lg:hidden">
                                <ul className="space-y-6 text-base">
                                    {resolvedMenuItems.map((item) => (
                                        <li key={`mobile-${item.href}`}>
                                            <Link href={item.href} className="text-muted-foreground hover:text-accent-foreground block duration-150">
                                                <span>{item.name}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {showAuthActions && (
                                <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit">
                                    <Button asChild variant="outline" size="sm" className={cn(isScrolled && showCtaOnScroll && 'lg:hidden')}>
                                        <a href={loginUrl}>
                                            <span>{t.common.login}</span>
                                        </a>
                                    </Button>
                                    <Button asChild size="sm" className={cn(isScrolled && showCtaOnScroll && 'lg:hidden')}>
                                        <a href={signUpUrl}>
                                            <span>{t.common.signUp}</span>
                                        </a>
                                    </Button>
                                    <Button asChild size="sm" className={cn(showCtaOnScroll && (isScrolled ? 'lg:inline-flex' : 'hidden'))}>
                                        <a href={appRedirectUrl}>
                                            <span>{t.common.getStarted}</span>
                                        </a>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    )
}
